import React, { useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import qs from "qs";
import useScript from "hooks/useScript";
import {
  selectProviderInfo,
  selectSelectionAccounts,
  selectFspId,
  selectUserId,
  selectPayerId,
  selectExternalDdaId,
  selectInstitution,
} from "state/selectors";
import {
  receiveTransaction,
  RECEIVED_TRANSACTION,
  logTrustlyEvent,
} from "state/actions/trustly";
import { selectSagaState } from "state/selectors/sagasInProgress";
import useIsWebView from "hooks/useIsWebView";
import AccountSelectionModal from "./AccountSelectionModal";
import LoadingDialog from "./LoadingDialog";
import {
  ERROR_IAV_STATUS,
  SUCCESS_IAV_STATUS,
  EXIT_IAV_STATUS,
  FAILED_TO_VERIFY,
  TRUSTLY_FAILED_TO_VERIFY,
  TRUSTLY_TIMEOUT,
  TRUSTLY_WATCHDOG_MS,
  TRUSTLY_PANEL_COMMAND_EVENT,
  TRUSTLY_PANEL_EVENT_NEW_LOCATION,
  TRUSTLY_PANEL_EVENT_CLOSE,
  TRUSTLY_PANEL_PAGE_ERROR,
  TRUSTLY_RETURN_URL,
  TRUSTLY_CANCEL_URL,
  MICRO_DEPOSIT_VERIFICATION_FAILED_STATUS,
  WATCHDOG_TIMEOUT_REASON,
} from "../consts";
import { isIAV, isMicroDepositRequestType } from "../utils";
import { ExtDdaAccount, TrustlyInfo } from "../types";
import useTrustlyWatchdog from "./hooks/useTrustlyWatchdog";
import useTrustlyOauthMessage from "./hooks/useTrustlyOauthMessage";

export const scriptId = "trustlyIFrame";

type Error = {
  errorCode?: string;
  message?: string;
};

// Reasons we surface through the optional `onCancel` callback. Hosts that
// supply onCancel get a structured signal for the various non-error,
// non-success exit paths Trustly can land on. Hosts that don't provide
// onCancel keep the legacy behavior (we fall back to onSuccess()).
export type TrustlyCancelReason =
  | "user_cancelled" // lightbox redirected to the cancel URL with no error code
  | "panel_closed" // panel close event with no status=5 and no error page
  | "user_aborted_loading"; // user clicked X on the post-transaction loading dialog

export type TrustlyCancelInfo = { reason: TrustlyCancelReason };

export type TrustlyIntegrationProps = {
  onError: (error?: Error) => void;
  onSuccess: (extDdas?: Array<ExtDdaAccount>) => void;
  // Optional. When omitted, cancel/abort paths fall back to onSuccess() to
  // preserve the historical contract (host SSO sees an empty success and
  // navigates back to its post-IAV screen). Provide onCancel to disambiguate.
  onCancel?: (info: TrustlyCancelInfo) => void;
  type: string;
  startAt: string;
  requestType: string;
};

declare global {
  interface Window {
    Trustly: {
      establish: Function;
      addPanelListener: Function;
      destroy: Function;
      proceedToChooseAccount: Function;
    };
  }
}

type TrustlyResponse = Event & {
  type: string;
  transactionId: string;
  page: string;
  data: string;
};

const stringifyObject = (object: any) => {
  if (!object || typeof object === "string") {
    return object;
  }
  try {
    return JSON.stringify(object);
  } catch (exception) {
    return "unable to stringify object";
  }
};

// Returns the URL origin (`https://host[:port]`) for `url`, or null when the
// input is missing or unparseable. Used to derive the Trustly origin from
// `trustlyInfo.endpoint` for postMessage validation.
const safeOrigin = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch (_) {
    return null;
  }
};

// Allows the lightbox-to-host postMessage signal `trustlyProceedToChooseAccount`
// only when it comes from our own page or the configured Trustly endpoint.
// Without this, any iframe on the page could force the lightbox to advance.
export const isAllowedTrustlyOrigin = (
  eventOrigin: string | undefined,
  trustlyEndpoint?: string | null
): boolean => {
  if (!eventOrigin) return false;
  if (
    typeof window !== "undefined" &&
    eventOrigin === window.location.origin
  ) {
    return true;
  }
  const trustlyOrigin = safeOrigin(trustlyEndpoint);
  return trustlyOrigin !== null && eventOrigin === trustlyOrigin;
};

const TrustlyIntegration = ({
  type,
  startAt,
  onError,
  onSuccess,
  onCancel,
  requestType,
}: TrustlyIntegrationProps) => {
  const iav = isIAV(type);
  const historyLengthRef = useRef(window.history.length);
  const { t } = useTranslation();
  const trustlyInfo = useSelector(selectProviderInfo) as TrustlyInfo;
  const fspId = useSelector(selectFspId);
  const payerId = useSelector(selectPayerId);
  const userId = useSelector(selectUserId);
  const externalDdaId = useSelector(selectExternalDdaId);
  const institution = useSelector(selectInstitution);
  const externalAccounts = useSelector(selectSelectionAccounts);
  const sagaState = useSelector((state) =>
    selectSagaState(RECEIVED_TRANSACTION, state)
  );
  const establishRef = useRef<boolean>(false);

  // One Trustly transaction = one host callback. Trustly often emits both a
  // `new_location` (cancel/return URL) and a follow-up `close` for the same
  // logical outcome, which previously caused us to call onError AND
  // onSuccess back-to-back. We flip this on the first terminal dispatch
  // and gate every host callback through it.
  const terminalFiredRef = useRef<boolean>(false);

  const isWebView = useIsWebView();

  const dispatch = useDispatch();

  // ** history solution **
  const handleHistory = (initialHistoryLength: number) => {
    if (window.location.pathname !== "blank") {
      const historyFix = initialHistoryLength - window.history.length;
      if (historyFix < 0) {
        window.history.go(historyFix);
      }
    }
  };

  // Consolidated `logTrustlyEvent` dispatcher. Every diagnostic log this
  // component emits shares the same fsp/payer/user/institution/request
  // context; the call sites differ only in `status` and the per-event
  // payload. Centralizing here removes ~70 lines of repeated boilerplate
  // and guarantees we don't accidentally drop a context field.
  const logEvent = useCallback(
    (
      status: string,
      extras: {
        transaction?: string;
        transactionId?: string;
      } = {}
    ) => {
      dispatch(
        logTrustlyEvent({
          useWinston: true,
          fspId: fspId!,
          userId: userId!,
          payerId: payerId!,
          externalDdaId,
          institution,
          requestType: type,
          startAt,
          status,
          ...extras,
        })
      );
    },
    [
      dispatch,
      externalDdaId,
      fspId,
      institution,
      payerId,
      startAt,
      type,
      userId,
    ]
  );

  // Fire-once dispatchers - every host callback path goes through one of
  // these. After the first terminal event we still log later panel events
  // (useful for diagnostics) but never call back into the host again.
  const fireSuccess = useCallback(
    (extDdas?: Array<ExtDdaAccount>) => {
      if (terminalFiredRef.current) return;
      terminalFiredRef.current = true;
      setTimeout(() => {
        handleHistory(historyLengthRef.current);
      }, 0);
      if (extDdas && extDdas.length > 0) {
        onSuccess(extDdas);
      } else {
        onSuccess();
      }
    },
    [onSuccess]
  );

  const fireError = useCallback(
    (err?: Error) => {
      if (terminalFiredRef.current) return;
      terminalFiredRef.current = true;
      onError(err);
    },
    [onError]
  );

  const fireCancellation = useCallback(
    (reason: TrustlyCancelReason) => {
      if (terminalFiredRef.current) return;
      terminalFiredRef.current = true;
      setTimeout(() => {
        handleHistory(historyLengthRef.current);
      }, 0);
      if (onCancel) {
        onCancel({ reason });
      } else {
        // Backward-compatible fallback: hosts that haven't adopted onCancel
        // continue to receive the historical empty-success signal.
        onSuccess();
      }
    },
    [onCancel, onSuccess]
  );

  useEffect(() => {
    if (sagaState?.data && !sagaState?.isError) {
      const { microDeposit, accounts } = sagaState.data;
      if (microDeposit) {
        logEvent(SUCCESS_IAV_STATUS, {
          transaction: JSON.stringify(accounts),
        });
        fireSuccess(accounts);
      }
    }
  }, [fireSuccess, sagaState, logEvent]);

  const handleLightBoxSuccess = useCallback(
    (data?: string) => {
      if (data) {
        logEvent(SUCCESS_IAV_STATUS, { transaction: data });
        dispatch(receiveTransaction({ transaction: data, type }));
      } else {
        // No transaction payload means the user backed out via the cancel
        // URL or an unknown panel state. Route through fireCancellation so
        // hosts that opted into onCancel get a structured signal; everyone
        // else still gets the historical empty-success.
        fireCancellation("user_cancelled");
      }
    },
    [dispatch, fireCancellation, logEvent, type]
  );

  const handleWatchdogFire = useCallback(() => {
    logEvent(ERROR_IAV_STATUS, {
      transaction: JSON.stringify({ reason: WATCHDOG_TIMEOUT_REASON }),
    });
    try {
      window.Trustly?.destroy?.();
    } catch (_) {
      // best-effort cleanup; ignore SDK-side errors
    }
    fireError({ errorCode: TRUSTLY_TIMEOUT });
  }, [fireError, logEvent]);

  const { start: startWatchdog, clear: clearWatchdog } = useTrustlyWatchdog(
    handleWatchdogFire,
    TRUSTLY_WATCHDOG_MS
  );

  const handleScriptLoad = useCallback(() => {
    if (!establishRef.current) {
      establishRef.current = true;
      const mobileOptions = isWebView
        ? {
            metadata: {
              urlScheme: `${trustlyInfo.pzIAVScheme ?? "pziavoauth"}://`,
              integrationContext: "InAppBrowser",
            },
          }
        : {};

      window.Trustly?.establish({
        ...trustlyInfo?.data,
        returnUrl: TRUSTLY_RETURN_URL,
        cancelUrl: TRUSTLY_CANCEL_URL,
        ...mobileOptions,
      });

      // Arm the watchdog only once we've actually called establish, so a
      // failed-to-load script (handled via useScript's onError below)
      // doesn't race with the timeout.
      startWatchdog();

      window.Trustly?.addPanelListener(
        (command: string, obj: TrustlyResponse) => {
          // The lightbox is alive - cancel the stuck-panel watchdog before
          // doing anything else, even for events we don't otherwise act on.
          clearWatchdog();
          switch (command) {
            case TRUSTLY_PANEL_COMMAND_EVENT:
              switch (obj.type) {
                case TRUSTLY_PANEL_EVENT_NEW_LOCATION: {
                  obj.preventDefault();
                  obj.stopPropagation();
                  if (obj.data?.indexOf(TRUSTLY_RETURN_URL) === 0) {
                    const transaction = obj.data.substring(
                      TRUSTLY_RETURN_URL.length + 1
                    );
                    logEvent(SUCCESS_IAV_STATUS, { transaction });
                    handleLightBoxSuccess(transaction);
                    break;
                  }
                  if (obj.data?.indexOf(TRUSTLY_CANCEL_URL) === 0) {
                    const transaction = obj.data.substring(
                      TRUSTLY_CANCEL_URL.length + 1
                    );
                    const { status } = qs.parse(transaction);
                    if (
                      isMicroDepositRequestType(requestType) &&
                      status === MICRO_DEPOSIT_VERIFICATION_FAILED_STATUS
                    ) {
                      logEvent(ERROR_IAV_STATUS, { transaction });
                      fireError({ errorCode: FAILED_TO_VERIFY });
                    } else {
                      logEvent(EXIT_IAV_STATUS, { transaction });
                    }
                    handleLightBoxSuccess();
                    break;
                  }
                  // Unknown new_location target.
                  logEvent(ERROR_IAV_STATUS, {
                    transaction: stringifyObject(obj),
                    transactionId: obj.transactionId,
                  });
                  fireError();
                  break;
                }
                case TRUSTLY_PANEL_EVENT_CLOSE: {
                  const transaction = obj.data?.substring(
                    TRUSTLY_CANCEL_URL.length + 1
                  );
                  const { status } = qs.parse(transaction ?? "");
                  if (status === MICRO_DEPOSIT_VERIFICATION_FAILED_STATUS) {
                    logEvent(ERROR_IAV_STATUS, {
                      transaction: stringifyObject({ command, transaction }),
                      transactionId: obj.transactionId,
                    });
                    fireError({ errorCode: TRUSTLY_FAILED_TO_VERIFY });
                    break;
                  }
                  if (obj.page === TRUSTLY_PANEL_PAGE_ERROR) {
                    logEvent(ERROR_IAV_STATUS, {
                      transaction: stringifyObject(obj),
                      transactionId: obj.transactionId,
                    });
                    fireError();
                  }
                  // Intentionally NO silent-close fallback here.
                  //
                  // Trustly's SDK emits `close` events whose semantics are
                  // ambiguous: it can fire AFTER a successful #success
                  // redirect as iframe cleanup, BEFORE new_location for
                  // some flows, or as a stand-alone "user dismissed the
                  // panel" signal. Without a reliable way to tell those
                  // apart from inside the SDK callback, treating any of
                  // them as a cancellation produces a real regression in
                  // the IAV add-account flow (the host receives a
                  // premature onSuccess() and unmounts before the saga
                  // finishes the `/auth/trustly/account/balance` round
                  // trip). The watchdog (TRUSTLY_WATCHDOG_MS) covers the
                  // truly-stuck case; this branch stays a no-op.
                  break;
                }
                default:
                  logEvent(SUCCESS_IAV_STATUS, {
                    transaction: stringifyObject(obj),
                    transactionId: obj.transactionId,
                  });
              }
              break;
            default:
              logEvent(SUCCESS_IAV_STATUS, {
                transaction: stringifyObject({ command, obj }),
                transactionId: obj?.transactionId,
              });
          }
        }
      );
    }
  }, [
    trustlyInfo,
    handleLightBoxSuccess,
    fireError,
    requestType,
    isWebView,
    clearWatchdog,
    startWatchdog,
    logEvent,
  ]);

  const handleLoadError = useCallback(() => {
    clearWatchdog();
    fireError({ message: t("error.unableToLoadScript") });
  }, [clearWatchdog, fireError, t]);

  useScript(
    trustlyInfo?.clientEndpoint,
    scriptId,
    handleScriptLoad,
    handleLoadError
  );

  const isAllowedOrigin = useCallback(
    (origin: string) => isAllowedTrustlyOrigin(origin, trustlyInfo?.endpoint),
    [trustlyInfo?.endpoint]
  );
  useTrustlyOauthMessage(isAllowedOrigin);

  // Tear down the lightbox + watchdog on unmount so we don't leak a live
  // Trustly iframe or a pending timer when the host SSO navigates away
  // mid-flow. `Trustly.destroy` is idempotent in practice; the configureStore
  // logout path also calls it, but that only fires on UNAUTHORIZED/LOGOUT.
  useEffect(
    () => () => {
      clearWatchdog();
      if (establishRef.current) {
        try {
          window.Trustly?.destroy?.();
        } catch (_) {
          // best-effort cleanup; ignore SDK-side errors
        }
      }
    },
    [clearWatchdog]
  );

  if (sagaState?.isRunning) {
    return (
      <LoadingDialog
        title={t("loadingDialog.title")}
        message={t("loadingDialog.message")}
        loading
        // The X on the loading dialog used to call handleSuccess([]) which
        // surfaced as an empty-success even though the user was actively
        // backing out of a server round-trip. Route through fireCancellation
        // so onCancel-aware hosts see 'user_aborted_loading' while everyone
        // else still falls back to the legacy onSuccess() signal.
        //
        // We also emit an EXIT log here so server-side observability is
        // uniform across every cancellation route - without this breadcrumb
        // a loading-dialog dismissal is indistinguishable from a genuine
        // empty-success when looking at logs alone.
        onClose={() => {
          logEvent(EXIT_IAV_STATUS, {
            transaction: JSON.stringify({ reason: "user_aborted_loading" }),
          });
          fireCancellation("user_aborted_loading");
        }}
      />
    );
  }
  if (sagaState?.isError || (externalAccounts && externalAccounts.length > 0)) {
    return (
      <AccountSelectionModal
        error={sagaState?.isError ? sagaState.data : undefined}
        onError={fireError}
        onSuccess={fireSuccess}
        retrievedAccounts={externalAccounts}
        multi={iav}
      />
    );
  }
  return <div />;
};

export default TrustlyIntegration;
